import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Columns } from "components/table";
import { DriverDraftRow } from "../types";
import { DriverCell, ActionCell } from "../components/table-cells";
import { addDriverPageRouteName } from "../../constants/routes";
import { dateTimeFormatter } from "_helper/timeFormatter";

interface UseDriverColumnsProps {
  t: any;
  handleDeleteRequest: (row: DriverDraftRow) => void;
}

export const useDriverColumns = ({
  t,
  handleDeleteRequest,
}: UseDriverColumnsProps) => {
  const navigate = useNavigate();

  return useMemo(
    (): Columns<DriverDraftRow>[] => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no") || "No.",
        width: "60px",
        sticky: "left",
      },
      {
        id: "driverName",
        fieldId: "driverName",
        label: t("col-driver") || "Driver",
        sortable: true,
        render: (row: DriverDraftRow) => <DriverCell row={row} />,
      },
      {
        id: "vendor",
        fieldId: "vendor",
        fieldId2: "vendorId",
        label: t("col-vendor") || "Vendor",
        sortable: true,
      },
      {
        id: "phone",
        fieldId: "phone",
        label: t("col-phone") || "Phone Number",
        sortable: true,
      },
      {
        id: "licenseType",
        fieldId: "licenseType",
        label: t("col-license") || "License Type",
        sortable: true,
      },
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by") || "Created By",
        sortable: true,
        render: (row: DriverDraftRow) => (
          <div className="flex flex-col">
            <span 
              className="text-xs text-text-secondary truncate max-w-[120px]" 
              title={row.createdBy}
            >
              {row.createdBy}
            </span>
            <span className="text-sm font-medium">
              {dateTimeFormatter(row.createdAt)}
            </span>
          </div>
        ),
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by") || "Updated By",
        sortable: true,
        render: (row: DriverDraftRow) => {
          const user = (row.updatedBy && row.updatedBy.trim() !== "" && row.updatedBy !== "-")
            ? row.updatedBy
            : (row.createdBy && row.createdBy !== "-" ? row.createdBy : "-");
          const rawDate = row.updatedAt || row.createdAt;
          const formattedDate = rawDate ? dateTimeFormatter(rawDate) : undefined;
          const hasUser = Boolean(user && user !== "-");
          const hasDate = Boolean(formattedDate && formattedDate !== "—" && formattedDate !== "-");
          if (!hasUser && !hasDate) {
            return "-";
          }
          return (
            <div className="flex flex-col">
              <span 
                className="text-xs text-text-secondary truncate max-w-[120px]" 
                title={user}
              >
                {user}
              </span>
              {hasDate && (
                <span className="text-sm font-medium">
                  {formattedDate}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action") || "Action",
        sticky: "right",
        render: (row: DriverDraftRow) => (
          <ActionCell
            row={row}
            t={t}
            handleEditRequest={(id) =>
              navigate(`${addDriverPageRouteName}/${id}`, { state: { from: "draft" } })
            }
            handleDeleteRequest={handleDeleteRequest}
          />
        ),
      },
    ],
    [t, navigate, handleDeleteRequest]
  );
};
